import { alpha } from '@mui/material/styles';

export type ColorSchema = 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error';

declare module '@mui/material/styles/createPalette' {
  interface TypeBackground {
    neutral: string;
  }
  interface SimplePaletteColorOptions {
    lighter: string;
    darker: string;
  }
  interface PaletteColor {
    lighter: string;
    darker: string;
  }

  interface Palette {
    greyScale: typeof greyScale;
    brand: typeof brand;
    extended: typeof extended;
    feedback: typeof feedback;
  }

  interface PaletteOptions {
    greyScale?: typeof greyScale;
    brand?: typeof brand;
    extended?: typeof extended;
    feedback?: typeof feedback;
  }
}

// SETUP COLORS

export const greyScale = {
  0: '#FFFFFF',
  50: '#E5E4E4',
  100: '#D1D1D1',
  200: '#919191',
  300: '#727272',
  400: '#5F5F5F',
  500: '#2D2D2D',
  600: '#191919',
  // 700: '#364657',
  // 800: '#0D1E30',
  // 900: '#08131F',
};

export const brand = {
  50: '#F3BBBE',
  100: '#E78C91',
  200: '#E35151',
  300: '#CF3C44',
  400: '#C4151F',
  500: '#A0191E',
  600: '#6E150E',
  700: '#3E0000',
};

export const extended = {
  100: '#788033',
  200: '#1C3D32',
  300: '#BC9B7C',
  400: '#F9E4A0',
};

export const feedback = {
  success: '#3A830D',
  info: '#0C71CE',
  warning: '#F6E546',
  danger: '#C83500',
};

export const grey = {
  0: '#FFFFFF',
  100: '#F3F8FB',
  200: '#E1ECF4',
  300: '#CEDBE4',
  400: '#A7BDCD',
  500: '#718EA3',
  600: '#4D677B',
  700: '#364657',
  800: '#0D1E30',
  900: '#08131F',
};

export const primary = {
  lighter: '#E78C91',
  light: '#CF3C44',
  main: '#C4151F',
  dark: '#A0191E',
  darker: '#6E150E',
  contrastText: '#FFFFFF',
};

export const secondary = {
  lighter: '#D4FFC2',
  light: '#82D660',
  main: '#54A932',
  dark: '#2F7712',
  darker: '#195004',
  contrastText: '#FFFFFF',
};

export const info = {
  lighter: '#D0F2FF',
  light: '#74CAFF',
  main: '#1890FF',
  dark: '#0C53B7',
  darker: '#04297A',
  contrastText: '#FFFFFF',
};

export const success = {
  lighter: '#D8FBDE',
  light: '#86E8AB',
  main: '#36B37E',
  dark: '#1B806A',
  darker: '#0A5554',
  contrastText: '#FFFFFF',
};

export const warning = {
  lighter: '#FFF7CD',
  light: '#FFE16A',
  main: '#FFC107',
  dark: '#B78103',
  darker: '#7A4F01',
  contrastText: '#232A33',
};

export const error = {
  lighter: '#FFE7D9',
  light: '#FFA48D',
  main: '#FF4842',
  dark: '#B72136',
  darker: '#7A0C2E',
  contrastText: '#FFFFFF',
};

export const common = {
  black: '#000000',
  white: '#FFFFFF',
};

export const action = {
  hover: alpha(grey[500], 0.08),
  selected: alpha(grey[500], 0.16),
  disabled: alpha(grey[500], 0.8),
  disabledBackground: alpha(grey[500], 0.24),
  focus: alpha(grey[500], 0.24),
  hoverOpacity: 0.08,
  disabledOpacity: 0.48,
};

const base = {
  primary,
  secondary,
  info,
  success,
  warning,
  error,
  grey,
  common,
  divider: alpha(grey[500], 0.24),
  action,
};

export function palette(mode: 'light' | 'dark') {
  const light = {
    ...base,
    mode: 'light',
    text: {
      primary: grey[800],
      secondary: grey[600],
      disabled: grey[500],
    },
    background: {
      paper: '#FFFFFF',
      default: '#FFFFFF',
      neutral: grey[100],
    },
    action: {
      ...base.action,
      active: grey[600],
    },
    greyScale,
    brand,
    extended,
    feedback,
  };

  const dark = {
    ...base,
    mode: 'dark',
    text: {
      primary: '#FFFFFF',
      secondary: grey[500],
      disabled: grey[600],
    },
    background: {
      paper: grey[800],
      default: grey[900],
      neutral: alpha(grey[500], 0.16),
    },
    action: {
      ...base.action,
      active: grey[500],
    },
    greyScale,
    brand,
    extended,
    feedback,
  };

  return mode === 'light' ? light : dark;
}
